// ===========================================
// Rich HTML comparison
// ===========================================
// Compares editor HTML against the SAVED record, ignoring differences that are
// pure serialization noise rather than real edits by the admin.

/**
 * Why this exists.
 *
 * The custom-email editor decides whether to block Send/Schedule by comparing
 * the form's content to the saved record. A byte-for-byte comparison can never
 * settle, because the two sides serialize the SAME document differently:
 *
 *   - Quill (`react-quill-new`) emits void elements unclosed — `<br>`, `<img …>`
 *     — and re-normalizes any HTML it is given back into that style on load.
 *   - The backend runs `sanitizeRichHtml` (sanitize-html) on save, which emits
 *     XHTML-style self-closed voids (`<br />`) and decodes HTML entities
 *     (`&#39;` → `'`, `&eacute;` → `é`, `&nbsp;` → a space).
 *
 * So saving `<p>a</p><p><br></p>` stores `<p>a</p><p><br /></p>`; the editor
 * reloads it, Quill rewrites it back to `<br>`, and the content is marked dirty
 * again the instant it loads. The campaign is then permanently "unsaved" and
 * Send Now / Schedule stay disabled no matter how many times the admin saves.
 *
 * ⚠️ THIS COMPARISON IS SAFETY-CRITICAL, so it is deliberately CONSERVATIVE.
 * Send passes only the campaign id — the server mails whatever was last SAVED.
 * A false "clean" therefore mails a STALE version to the entire audience, and
 * email cannot be unsent. So this normalizes ONLY the two transformations the
 * sanitizer actually performs, and nothing else.
 *
 * In particular it does NOT collapse whitespace. That was tried and removed:
 * `sanitizeRichHtml` was verified to preserve whitespace byte-for-byte, so
 * collapsing bought nothing, while it silently equated genuinely different
 * documents — `<strong>a</strong> <em>b</em>` vs `<strong>a</strong><em>b</em>`,
 * and any change inside `<pre>`/`<code>`. Every relaxation here must be
 * justified by a transformation the sanitizer really makes.
 */

/** Reused across calls — creating one per text node dominated the cost. */
let decoderEl: HTMLTextAreaElement | null = null;

/**
 * Decode HTML entities exactly as a browser would.
 *
 * A hand-maintained entity table would be wrong the moment it drifted:
 * sanitize-html decodes every named entity EXCEPT the XML-significant four
 * (`&amp;` `&lt;` `&gt;` and, in text, the quote forms), which is far too long
 * a list to keep by hand. Round-tripping through the DOM inherits the browser's
 * own table, so it cannot drift.
 *
 * Safe against injection: the string is assigned to a detached `<textarea>`,
 * whose content model is raw text — no markup inside it is ever parsed as
 * elements, and the node is never attached to the document.
 */
function decodeEntities(text: string): string {
  // Fast path: no entity, nothing to decode. This is the overwhelmingly common
  // case, and it skips the HTML parser entirely — parsing is the expensive part,
  // and this runs on every render.
  if (text.indexOf('&') === -1) return text;

  // One reused detached element rather than one per text node.
  decoderEl = decoderEl ?? document.createElement('textarea');
  decoderEl.innerHTML = text;
  const decoded = decoderEl.value;

  // Re-escape ONLY the two characters that could turn decoded text into markup.
  //
  // Without this, an admin who types the literal characters `<strong>` (stored
  // as `&lt;strong&gt;`) decodes to real-looking markup and compares EQUAL to
  // genuinely bold text — a false clean, which mails a stale campaign.
  //
  // `&` is deliberately NOT re-escaped: sanitize-html leaves `&amp;` encoded on
  // both sides, so it already matches, and re-escaping it here would undo the
  // decode of every other entity (`&#39;` → `'` → `&amp;#39;`).
  return decoded.replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

/**
 * Strip a trailing `/` (and surrounding whitespace) from a void tag's attribute
 * text, so `<br />` and `<br>` reduce to the same thing.
 *
 * Done by scanning rather than with a regex on purpose. The obvious pattern
 * (`([^>]*?)\s*\/\s*>`) backtracks quadratically over a long whitespace run
 * inside a tag — a padded `alt` measured ~900ms at 40k spaces and ~20s at 200k —
 * and this runs on every render.
 */
function stripSelfClosingSlash(attrs: string): string {
  const isSpace = (c: string) => /\s/.test(c);
  let end = attrs.length;
  while (end > 0 && isSpace(attrs[end - 1])) end--;
  if (end > 0 && attrs[end - 1] === '/') {
    end--;
    while (end > 0 && isSpace(attrs[end - 1])) end--;
  }
  return attrs.slice(0, end);
}

/**
 * Sort a tag's attributes and canonicalize its inline `style`.
 *
 * Two serialization differences live here, neither of them a content change:
 *
 *  - ATTRIBUTE ORDER. `sanitizeRichHtml` appends `target`/`rel` to every `<a>`
 *    via `transformTags`, and Quill re-emits them alphabetically (`rel` before
 *    `target`). Same link, different byte order — so ANY email containing a
 *    hyperlink compared unequal forever.
 *  - STYLE SPACING. Quill emits `style="color: rgb(255, 0, 0);"`; sanitize-html
 *    reserializes it as `style="color:rgb(255, 0, 0)"` — a space and a trailing
 *    semicolon apart. Reachable from the toolbar's colour and highlight pickers.
 *
 * Only the tag's own punctuation is touched; attribute VALUES are compared
 * intact, so a changed `href` or a changed colour still reads as an edit.
 */
function canonicalizeTag(tag: string): string {
  // Leave closing tags, comments and doctypes alone.
  const open = /^<\s*([a-zA-Z][a-zA-Z0-9-]*)\b([\s\S]*?)(\/?)>$/.exec(tag);
  if (!open) return tag;

  const [, name, attrText, selfClose] = open;
  const attrs: string[] = [];
  const attrRe = /([a-zA-Z_:][-a-zA-Z0-9_:.]*)(?:\s*=\s*("[^"]*"|'[^']*'|[^\s"'>]+))?/g;
  let m: RegExpExecArray | null;
  while ((m = attrRe.exec(attrText)) !== null) {
    const attrName = m[1].toLowerCase();

    // Drop the editor's own scaffolding. Quill re-adds `contenteditable` on
    // every load and the sanitizer never stores it (deliberately — the same
    // sanitizer guards public website blocks), so the two sides could never
    // agree. It renders nothing in an email client: chrome, not content.
    //
    // Dropped HERE, inside the attribute walk, rather than with a document-wide
    // regex: a leading `\s*` in such a pattern backtracks quadratically across a
    // long whitespace run inside a tag (measured 3.9s at 50k spaces, 40s at
    // 200k). This walk already has the attributes parsed, so it costs nothing.
    if (attrName === 'contenteditable') {
      // Skip the value too, if it has one.
      continue;
    }

    let value = m[2];
    if (value === undefined) {
      attrs.push(attrName);
      continue;
    }
    if (
      (value.startsWith('"') && value.endsWith('"')) ||
      (value.startsWith("'") && value.endsWith("'"))
    ) {
      value = value.slice(1, -1);
    }
    if (attrName === 'style') {
      // `a: b; c: d;` → `a:b;c:d` — declaration order is preserved (it is
      // meaningful in CSS), only the punctuation is normalized.
      value = value
        .split(';')
        .map((decl) => decl.trim())
        .filter(Boolean)
        .map((decl) => {
          const colon = decl.indexOf(':');
          if (colon === -1) return decl;
          return `${decl.slice(0, colon).trim().toLowerCase()}:${decl.slice(colon + 1).trim()}`;
        })
        // Quill writes a hard-coded `border: 1px solid #000` on every `<table>`
        // (quill/core/editor.js) and the sanitizer stores none (`border` is not an
        // allowed style), so a saved table could never compare equal. Cell borders
        // are added at send time; the table's own is never content.
        .filter((decl) => !(name.toLowerCase() === 'table' && /^border(-|:)/.test(decl)))
        .join(';');
      if (value === '' && name.toLowerCase() === 'table') continue;
    }
    attrs.push(`${attrName}="${value}"`);
  }

  attrs.sort();
  return `<${name.toLowerCase()}${attrs.length ? ' ' + attrs.join(' ') : ''}${selfClose}>`;
}

/**
 * Reduce rich-text HTML to a form where only MEANINGFUL differences remain.
 */
export function normalizeRichHtml(html: string | null | undefined): string {
  if (!html) return '';

  return (
    html
      // Drop Quill's list-marker element. It carries no content — the editor
      // re-creates it from `data-list` on every load — and the sanitizer stores
      // it stripped of its `contenteditable`, so it never matches byte-for-byte.
      // (`contenteditable` itself is dropped per-attribute in `canonicalizeTag`.)
      .replace(/<span class="ql-ui"[^>]*><\/span>/gi, '')
      // Self-closed voids → unclosed, so `<br />` and `<br>` compare equal.
      // Quill emits the unclosed form; sanitize-html emits the closed one.
      .replace(/<\s*(br|hr|img)\b([^>]*)>/gi, (_match, tag: string, attrs: string) =>
        `<${tag}${stripSelfClosingSlash(attrs)}>`
      )
      // Decode entities in TEXT ONLY, never inside a tag. Attribute values keep
      // their entities (`href="?a=1&amp;b=2"` must not become `&`), and decoding
      // inside a tag could otherwise fabricate or destroy markup.
      // Tags additionally get their attributes sorted and styles canonicalized.
      .replace(/(<[^>]*>)|([^<]+)/g, (_match, tag: string | undefined, text: string | undefined) =>
        tag ? canonicalizeTag(tag) : decodeEntities(text ?? '')
      )
  );
}

/** True when two rich-text values differ by more than serialization style. */
export function richHtmlEqual(a: string | null | undefined, b: string | null | undefined): boolean {
  return normalizeRichHtml(a) === normalizeRichHtml(b);
}
